"use client"

import * as React from "react"
import { Popover } from "@base-ui/react/popover"
import { Check, ChevronDown, Plus } from "lucide-react"
import { cn } from "@/shared/lib/utils"

export interface CreatableSelectOption {
  value: string
  label: string
  disabled?: boolean
}

interface CreatableSelectProps {
  value: string
  onValueChange: (value: string) => void
  /**
   * Called when the user CREATES a new option (Enter / click on the create
   * control), as distinct from selecting an existing one. When provided, the
   * create flow calls this with the typed label instead of committing the raw
   * label as the value - the caller persists the new option and drives the
   * selected value itself. When omitted, creating commits the typed label as
   * the value (free-text behaviour).
   */
  onCreate?: (label: string) => void
  options: CreatableSelectOption[]
  placeholder?: string
  /** Label shown on the collapsed "create" control at the bottom of the list. */
  createLabel?: string
  /** Placeholder shown once the create control is expanded into a text field. */
  createPlaceholder?: string
  disabled?: boolean
  size?: "sm" | "default"
  className?: string
  triggerClassName?: string
  contentClassName?: string
  maxLength?: number
  "data-testid"?: string
}

function CreatableSelect({
  value,
  onValueChange,
  onCreate,
  options,
  placeholder = "Select...",
  createLabel = "Create new",
  createPlaceholder = "Type and press Enter",
  disabled,
  size = "default",
  className,
  triggerClassName,
  contentClassName,
  maxLength,
  "data-testid": testId,
}: CreatableSelectProps) {
  const [open, setOpen] = React.useState(false)
  const [creating, setCreating] = React.useState(false)
  const [customLabel, setCustomLabel] = React.useState("")
  const listRef = React.useRef<HTMLDivElement>(null)

  const selectedOption = React.useMemo(
    () => options.find((o) => o.value === value),
    [options, value],
  )

  const reset = React.useCallback(() => {
    setCreating(false)
    setCustomLabel("")
  }, [])

  const commit = React.useCallback(
    (val: string) => {
      onValueChange(val)
      setOpen(false)
      reset()
    },
    [onValueChange, reset],
  )

  const handleCreate = React.useCallback(() => {
    const trimmed = customLabel.trim()
    if (!trimmed) return
    if (onCreate) {
      // Caller persists the new option and sets the selected value itself;
      // just close + reset the create control here.
      onCreate(trimmed)
      setOpen(false)
      reset()
      return
    }
    commit(trimmed)
  }, [customLabel, commit, onCreate, reset])

  const startCreating = React.useCallback(() => {
    setCreating(true)
  }, [])

  // Arrow keys move focus between options (native listbox-style roving).
  const moveFocus = React.useCallback((direction: 1 | -1) => {
    const items = Array.from(
      listRef.current?.querySelectorAll<HTMLElement>(
        "[data-item]:not([disabled])",
      ) ?? [],
    )
    if (items.length === 0) return
    const current = items.indexOf(document.activeElement as HTMLElement)
    const next = (current + direction + items.length) % items.length
    items[next]?.focus()
  }, [])

  const displayLabel = value ? (selectedOption?.label ?? value) : null

  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) reset()
      }}
    >
      <Popover.Trigger
        data-slot="creatable-select-trigger"
        data-size={size}
        data-testid={testId}
        disabled={disabled}
        className={cn(
          "flex w-full items-center justify-between gap-1.5 rounded-field border border-input bg-[var(--bg-light)] py-2 pr-2 pl-2.5 text-base whitespace-nowrap transition-colors outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 data-placeholder:text-muted-foreground data-[size=default]:h-8 data-[size=sm]:h-7 dark:bg-input/30 dark:hover:bg-input/50",
          className,
          triggerClassName,
        )}
      >
        <span
          className={cn(
            "min-w-0 flex-1 truncate text-left",
            !displayLabel && "text-muted-foreground",
          )}
        >
          {displayLabel ?? placeholder}
        </span>
        <ChevronDown className="pointer-events-none size-4 shrink-0 text-muted-foreground" />
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Positioner
          side="bottom"
          sideOffset={4}
          align="start"
          className="isolate z-50"
        >
          <Popover.Popup
            data-slot="creatable-select-content"
            className={cn(
              "w-(--anchor-width) min-w-[220px] origin-(--transform-origin) overflow-hidden rounded-[8px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] shadow-[var(--shadow-float)] duration-100 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
              contentClassName,
            )}
          >
            {options.length > 0 && (
              <div
                ref={listRef}
                role="listbox"
                onKeyDown={(e) => {
                  if (e.key === "ArrowDown") {
                    e.preventDefault()
                    moveFocus(1)
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault()
                    moveFocus(-1)
                  }
                }}
                className="max-h-[240px] overflow-y-auto p-1"
              >
                {options.map((option) => {
                  const selected = option.value === value
                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      data-item
                      disabled={option.disabled}
                      onClick={() => {
                        if (!option.disabled) commit(option.value)
                      }}
                      className={cn(
                        "relative flex w-full cursor-default items-center gap-1.5 rounded-md py-1.5 pr-8 pl-2 text-left text-base text-[var(--ink-2)] outline-hidden select-none hover:bg-[var(--bg-subtle)] focus-visible:bg-[var(--bg-subtle)] disabled:pointer-events-none disabled:opacity-50",
                        selected && "bg-[var(--bg-subtle)] font-medium text-[var(--ink)]",
                      )}
                    >
                      {option.label}
                      {selected && (
                        <span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center">
                          <Check className="size-3.5" />
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            )}

            {/* Create control: an input-like button that becomes a text field. */}
            <div
              className={cn(
                "p-1.5",
                options.length > 0 && "border-t-[0.5px] border-[var(--rule)]",
              )}
            >
              {creating ? (
                <input
                  type="text"
                  autoFocus
                  value={customLabel}
                  maxLength={maxLength}
                  placeholder={createPlaceholder}
                  onChange={(e) =>
                    setCustomLabel(
                      maxLength
                        ? e.target.value.slice(0, maxLength)
                        : e.target.value,
                    )
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault()
                      handleCreate()
                    } else if (e.key === "Escape") {
                      e.preventDefault()
                      e.stopPropagation()
                      reset()
                    }
                  }}
                  className="flex h-8 w-full items-center rounded-field border border-[var(--green)] bg-[var(--bg-light)] px-2.5 text-base text-[var(--ink)] outline-none transition-colors placeholder:text-muted-foreground dark:bg-input/30"
                />
              ) : (
                <button
                  type="button"
                  onClick={startCreating}
                  className="flex h-8 w-full items-center gap-2 rounded-field border border-input bg-[var(--bg-light)] px-2.5 text-base text-muted-foreground outline-none transition-colors hover:border-ring/60 hover:text-[var(--ink-2)] focus-visible:border-[var(--green)] dark:bg-input/30"
                >
                  <Plus className="size-3.5 shrink-0" />
                  <span className="truncate">{createLabel}</span>
                </button>
              )}
            </div>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  )
}

export { CreatableSelect }
